import { INSURANCE_PAY_FOR_MY_SELF } from 'components/DentalMap/utils/constant';
import {
  ICreateFamilyBookingData,
  IFamilyBookingPatientPayload,
} from 'interfaces/createBookingDataTypes';
import { ContactUser, SelectedPatient } from 'interfaces/familyBookingTypes';
import { parseTrafficSource } from 'utils/parseQueryUrl';

import { ModeOption } from '../../utils/constants';
import { hasHomeAddressOnFile } from './utils';

export const MISSING_SLOT_ERROR_MESSAGE =
  'Something went wrong. Please try again later';

interface IBuildFamilyBookingPayloadParams {
  clinicId: string;
  contactUser: ContactUser;

  bookablePatients: SelectedPatient[];

  note: string;
  homeAddress: string;
  postalCode: string;
  isLoggedIn: boolean;

  search: string;
  insurance: { id: string; name: string } | null;
  isPayForMyself: boolean;
  insurancePaymentMethod: ModeOption | null;
}

export const buildFamilyBookingPayload = ({
  clinicId,
  contactUser,
  bookablePatients,
  note,
  homeAddress,
  postalCode,
  isLoggedIn,
  search,
  insurance,
  isPayForMyself,
  insurancePaymentMethod,
}: IBuildFamilyBookingPayloadParams): ICreateFamilyBookingData => {
  const searchParams = new URLSearchParams(search);

  const code = searchParams.get('code');

  const isGoogleBookOnline = searchParams.get('rwg_token');

  const source = parseTrafficSource(search);

  const trimmedNote = note.trim();

  const trimmedHomeAddress = homeAddress.trim();

  const trimmedPostalCode = postalCode.trim();

  const patients = bookablePatients.map(
    (patient): IFamilyBookingPatientPayload => {
      if (
        !patient.doctorId ||
        !patient.operatoryId ||
        !patient.appointmentDate ||
        !patient.startTime ||
        !patient.dentist ||
        !patient.patientFamilyBookingSessionId
      ) {
        throw new Error(MISSING_SLOT_ERROR_MESSAGE);
      }

      const isHomeAddressForwarded =
        !!trimmedHomeAddress && !hasHomeAddressOnFile(patient);

      return {
        ...(patient.id && { id: patient.id }),
        firstName: patient.firstName,
        lastName: patient.lastName,
        dob: patient.dob,
        email: patient.email,
        phoneNumber: patient.phoneNumber,
        serviceId: (patient.bookingServiceId || patient.serviceId) as string,
        doctorId: patient.doctorId,
        operatoryId: patient.operatoryId,
        appointmentDate: patient.appointmentDate,
        startTime: patient.startTime,
        useInsurance: false,
        isPreferredDoctor: Boolean(patient.preferredPractitionerId),
        patientFamilyBookingSessionId: patient.patientFamilyBookingSessionId,
        dentist: patient.dentist,
        ...(trimmedNote && { patientNote: trimmedNote }),
        ...(isHomeAddressForwarded && {
          homeAddress: trimmedHomeAddress,
          postalCode: trimmedPostalCode,
        }),
        ...(patient.chairOptimizations && {
          chairOptimizations: patient.chairOptimizations,
        }),
      };
    }
  );

  return {
    isClinicBooking: true,
    clinicId,
    isSignedIn: Boolean(isLoggedIn),
    pointOfContact: {
      firstName: contactUser.firstName,
      lastName: contactUser.lastName,
      phoneNumber: contactUser.phoneNumber,
      dob: contactUser.dob,
      email: contactUser.email,
    },
    patients,
    ...(code && { code }),
    ...(source && { source }),
    ...(isGoogleBookOnline && { isGoogleBookOnline: true }),
    ...(insurance && {
      clinicInsuranceId: insurance.id,
      insuranceName: insurance.name,
    }),
    ...(isPayForMyself && {
      insuranceName: INSURANCE_PAY_FOR_MY_SELF,
      insurancePaymentMethod: insurancePaymentMethod?.id || null,
    }),
  };
};
